import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { BadgeIcon } from "@/components/Icon/BadgeIcon";
import { Text } from "@/components/Typography/Text";
import { cn } from "@/lib/cn";

import { Card } from "../../Card/card";
import { HStack, VStack } from "../../Layout";

// 小分類用の型定義
type StepperSubSection = {
  label: string;
  hash: string;
  completed?: boolean;
  error?: boolean;
};

// 中分類用の型定義
type StepperSection = {
  label: string;
  hash?: string;
  completed?: boolean;
  error?: boolean;
  // 小分類のセクション
  subSections?: StepperSubSection[];
};

// 大分類用の型定義
type StepperCategory = {
  label: string;
  // 中分類のセクション
  sections: StepperSection[];
};

// フラットモード用のセクション型定義
type SimpleSection = {
  label: string;
  hash: string;
  completed?: boolean;
  error?: boolean;
};

type BaseProps = {
  link: {
    hash: string;
    to: string;
    search?: { [key: string]: string };
  };
  title?: string;
  className?: string;
};

// 階層モードのprops
type HierarchicalProps = BaseProps & {
  categories: StepperCategory[];
  sections?: never; // categoriesとsectionsを同時に指定できないようにする
  indexMode?: "all" | "subSection"; // BadgeIconのcount計算モード
};

// フラットモードのprops
type FlatProps = BaseProps & {
  sections: SimpleSection[];
  categories?: never; // categoriesとsectionsを同時に指定できないようにする
};

// Props型はHierarchicalPropsまたはFlatPropsのどちらか
type Props = HierarchicalProps | FlatProps;

/**
 * ステッパーナビゲーションコンポーネント
 *
 * 2つの表示モードをサポート:
 * 1. フラットモード: シンプルな1階層のステップナビゲーション
 * 2. 階層モード: 大分類・中分類・小分類の3階層構造のナビゲーション
 */
export default function StepperNavigation(props: Props) {
  const { link, title, className } = props;
  const [activeHash, setActiveHash] = useState<string>(link.hash);

  // indexModeのデフォルト値を設定（未指定の場合はsubSection）
  const indexMode =
    "indexMode" in props ? props.indexMode || "subSection" : "subSection";

  // クライアントサイドでのナビゲーション時にhashを監視して更新する
  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = location.hash.slice(1);
      if (currentHash) {
        setActiveHash(currentHash);
      }
    };

    // 初期値を設定
    handleHashChange();

    // hashの変更を監視
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // propのlinkが変更された場合も更新
  useEffect(() => {
    if (link.hash) {
      setActiveHash(link.hash);
    }
  }, [link.hash]);

  // フラットモードのデータを階層モードに変換する
  const categories = props.categories
    ? props.categories
    : [
        {
          label: "",
          sections: [
            {
              label: "",
              subSections: props.sections,
            },
          ],
        },
      ];

  return (
    <Card
      className={cn(
        "h-[756px] min-w-[280px] rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md",
        className,
      )}
    >
      <VStack className="gap-6 p-6">
        {title && (
          <div className="flex flex-col items-start gap-1.5 self-stretch px-0">
            <Text variant="h3"> {title}</Text>
          </div>
        )}
        <VStack className="gap-2">
          {(() => {
            // indexMode="all"の場合、全体を通して連続したカウントを計算
            let globalIndex = 0;

            return categories.map((category, categoryIndex) => (
              <VStack
                key={category.label || `category-${categoryIndex}`}
                className="gap-2 w-full"
              >
                {/* 大分類ラベル（フラットモードでは非表示） */}
                {category.label && (
                  <Text
                    variant="label"
                    className="text-sm font-semibold text-neutral-on-fill"
                  >
                    {category.label}
                  </Text>
                )}

                {/* 中分類のセクションまたはフラットモードのセクション */}
                {category.sections.map((section, sectionIndex) => (
                  <VStack
                    key={section.label || `section-${sectionIndex}`}
                    className="w-full gap-1"
                  >
                    {/* 中分類ラベル（フラットモードでは非表示） */}
                    {section.label && (
                      <Text
                        variant="label"
                        className="text-sm font-medium text-neutral-on-fill pl-4"
                      >
                        {section.label}
                      </Text>
                    )}

                    {/* 小分類のサブセクション または フラットモードのセクション */}
                    <VStack className={section.label ? "pl-8 gap-1" : "gap-1"}>
                      {section.subSections?.map((subSection, subIndex) => {
                        // カウントの計算
                        const count =
                          indexMode === "all" ? ++globalIndex : subIndex + 1;

                        return (
                          <Link
                            key={subSection.hash}
                            to={link.to}
                            hash={subSection.hash}
                            search={link.search}
                            className={`p-2 ${
                              activeHash === subSection.hash && subSection.error
                                ? "text-error-on-muted-fill rounded-md bg-error-muted-fill font-semibold"
                                : activeHash === subSection.hash
                                  ? "rounded-md bg-primary-muted-fill font-semibold text-primary"
                                  : "text-muted-foreground hover:bg-primary-muted-fill hover:text-primary hover:shadow-xs"
                            }`}
                            onClick={() => setActiveHash(subSection.hash)}
                          >
                            <HStack className="items-center gap-1.5">
                              {subSection.error ? (
                                <BadgeIcon type="error" />
                              ) : subSection.completed ? (
                                <BadgeIcon type="check" />
                              ) : (
                                <BadgeIcon
                                  count={count}
                                  type={
                                    activeHash === subSection.hash
                                      ? "primary"
                                      : "neutral"
                                  }
                                />
                              )}
                              <Text
                                variant="label"
                                className={`text-sm ${
                                  activeHash === subSection.hash
                                    ? "text-primary-on-neutral-fill"
                                    : "text-neutral-muted-on-fill"
                                }`}
                              >
                                {subSection.label}
                              </Text>
                            </HStack>
                          </Link>
                        );
                      })}
                    </VStack>
                  </VStack>
                ))}
              </VStack>
            ));
          })()}
        </VStack>
      </VStack>
    </Card>
  );
}
